import React, { useCallback, useMemo } from 'react';
import { generatePath, useHistory } from 'react-router';

import { OrderAction } from 'common/api/autogenerated/trading-gateway';

import { Button, SvgIconUrl } from 'common/components';

import { useBowser, useI18n, usePopup } from 'common/hooks';
import { ModalAction } from 'common/redux/modal/initialState';
import svgIcons from 'common/svgIcons';

import { routes } from 'routes/routes.constants';

import './index.css';

interface Props {
  action: OrderAction;
  baseCurrency: string;
  quotedCurrency: string;
  amount: number;
  type?: 'spot' | 'derivative';
}

export const DerivativeOrderFullyExecutedPopupComponent: React.FC<React.PropsWithChildren<Props>> = ({
  baseCurrency,
  quotedCurrency,
  action,
  amount,
  type = 'spot',
}) => {
  const { translateToText } = useI18n();
  const { hide } = usePopup();
  const { isMobile } = useBowser();
  const history = useHistory();

  const symbol = useMemo(() => `${baseCurrency}/${quotedCurrency}`, [baseCurrency, quotedCurrency]);

  const messageText = useMemo(
    () =>
      translateToText('mobile:order:price-change-caution:fully')
        .replace('{DIRECTION}', action.toUpperCase())
        .replace('{PAIR}', symbol),
    [action, symbol, translateToText],
  );

  const operationNameText = useMemo(
    () =>
      translateToText(
        action === OrderAction.BUY
          ? 'mobile:order:price-change-caution:received-label'
          : 'mobile:order:price-change-caution:spent-label',
      ),
    [action, translateToText],
  );

  const targetUrl = useMemo(
    () => (type === 'spot' ? routes.getSpotAssetPage() : routes.getDerivativesAssetPage()),
    [type],
  );

  const goToCreateNewOrder = useCallback(() => {
    hide();
    history.push(
      generatePath(targetUrl, {
        symbol: `${baseCurrency}-${quotedCurrency}`,
        direction: action,
      }),
    );
  }, [action, targetUrl, baseCurrency, hide, history, quotedCurrency]);

  const onOkClickHandler = useCallback(() => {
    hide();
    if (isMobile) {
      history.push(
        generatePath(targetUrl, {
          symbol: `${baseCurrency}-${quotedCurrency}`,
        }),
      );
    }
  }, [baseCurrency, targetUrl, hide, history, isMobile, quotedCurrency]);

  return (
    <div className='derivative-order-fully-executed-popup__wrapper'>
      <div className='icon-shadow derivative-order-fully-executed-popup__icon'>
        <SvgIconUrl symbol={svgIcons.Check()} />
      </div>
      <p className='derivative-order-fully-executed-popup__message'>{messageText}</p>
      <div className='derivative-order-fully-executed-popup__value-holder'>
        <p className='derivative-order-fully-executed-popup__value-title'>{operationNameText}:</p>
        <p className='derivative-order-fully-executed-popup__value'>
          {amount} {baseCurrency}
        </p>
      </div>
      <div className='derivative-order-fully-executed-popup__buttons'>
        <Button outline large stretch onClick={onOkClickHandler}>
          {translateToText('mobile:order:price-change-caution:fully:ok-btn')}
        </Button>
        <Button large stretch onClick={goToCreateNewOrder}>
          {translateToText('mobile:order:price-change-caution:fully:new-order-btn')}
        </Button>
      </div>
    </div>
  );
};

const config: ModalAction = {
  wrapperClassName: 'derivative-order-fully-executed-popup',
  hideCloseIcon: false,
  animation: 'appearUp',
  title: '',
};

export default (props: Props) => ({
  component: <DerivativeOrderFullyExecutedPopupComponent {...props} />,
  onCloseHandler: () => null,
  ...config,
});
